/**
 * Application Constants
 * Centralized constant values used throughout the application.
 */

/**
 * Application metadata
 */
export const APP_NAME = 'Dream System';
export const APP_VERSION = '1.0.0';

/**
 * API configuration
 */
export const API_TIMEOUT = 30000; // 30 seconds
/** Timeout for file uploads (e.g. video/document) in ms - 5 minutes */
export const API_UPLOAD_TIMEOUT = 300000;
export const API_RETRY_COUNT = 3;

/**
 * Pagination defaults
 */
export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/**
 * Date formats
 */
export const DATE_FORMAT = 'DD/MM/YYYY';
export const DATETIME_FORMAT = 'DD/MM/YYYY HH:mm';
export const TIME_FORMAT = 'HH:mm';

/**
 * Storage keys
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'dream_system_auth_token',
  REFRESH_TOKEN: 'dream_system_refresh_token',
  USER_DATA: 'dream_system_user_data',
  THEME: 'dream_system_theme',
  LANGUAGE: 'dream_system_language',
  KPI_FLAGGED_AGENT_IDS: 'dream_kpi_compliance_flagged_agent_ids',
} as const;

/**
 * Route paths
 */
export const ROUTES = {
  // Public routes
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  ONBOARDING: '/onboarding',

  // Protected routes
  DASHBOARD: '/dashboard',

  // Registration module
  REGISTRATION: '/registration',
  REGISTRATION_MANAGEMENT: '/registration/management',
  LINK_REGISTRATIONS: '/registration/links/:linkId/registrations',
  CONTRACT_TEMPLATES: '/contract-templates',
  PRODUCT_CONFIG: '/product-config',

  // Dashboard Daie module
  DASHBOARD_DAIE: '/dashboard-daie',
  INCENTIVES: '/incentives',
  INCENTIVE_HISTORY: '/incentives/history',
  PROFILE: '/profile',

  // Sales module
  SALES: '/sales',
  SALES_PIPELINE: '/sales/pipeline',
  SALES_LEADS: '/sales/leads',
  SALES_APPOINTMENTS: '/sales/appointments',
  SALES_FARAID: '/sales/faraid',
  SALES_CLOSING: '/sales/closing',
  SALES_QUOTATIONS: '/sales/quotations',

  // Training/LMS module
  TRAINING: '/training',
  TRAINING_COURSES: '/training/courses',
  TRAINING_COURSE_ENROLLMENTS: '/training/courses/:courseId/enrollments',
  TRAINING_PROGRESS: '/training/progress',
  TRAINING_PHYSICAL_PROGRAMS: '/training/physical-programs',
  TRAINING_MY_PROGRAMS: '/training/my-programs',
  TRAINING_PHYSICAL_PROGRAM_SCAN: '/training/physical-programs/:programId/scan',

  // Dashboard Report module
  ADMIN_DASHBOARD: '/admin/dashboard',
  DASHBOARD_REPORT: '/dashboard-report',
  DASHBOARD_REPORT_SALES: '/dashboard-report/sales',
  DASHBOARD_REPORT_CONTRACT: '/dashboard-report/contract',
  DASHBOARD_REPORT_TRAINING: '/dashboard-report/training',
  DASHBOARD_REPORT_EXPORT: '/dashboard-report/exportable-data',
  SYSTEM_CONFIG: '/system-config',
  MANAGER_OVERVIEW: '/manager/overview',
  AGENT_MONITORING: '/agent-monitoring',
  KPI_COMPLIANCE: '/kpi-compliance',
  KPI_CONFIGURATION: '/kpi-configuration',
  EXECUTIVE_DASHBOARD: '/executive-dashboard',
  DAIE_MANAGEMENT: '/daie-management',
  HQ_REPORT_DOWNLOAD: '/report-download',
  ROLES_MANAGEMENT: '/roles',

  // Memo module
  MEMO: '/memo',
  NOTIFICATIONS: '/notifications',

  // Chatbot module
  CHATBOT: '/chatbot',
  
  // Support Inquiries (Admin)
  SUPPORT_INQUIRIES: '/support-inquiries',
} as const;

/**
 * User roles
 */
export const USER_ROLES = {
  DAIE: 'daie',
  MANAGER: 'manager',
  ADMIN: 'admin',
  EXECUTIVE: 'executive',
} as const;

/**
 * Status values
 */
export const STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;
